import React from 'react';
import { useCurrentUser } from '@mp/hooks';
import useSocialProofMarketActivity, {
  SocialProofVariant,
} from '../../hooks/useSocialProofMarketActivity';

import HighVolume from './HighVolume';
import LowVolume from './LowVolume';
import NoJobs from './NoJobs';

interface SocialProofBannerProps {
  waitForUser?: boolean;
}

export const SocialProofBanner: React.FC<SocialProofBannerProps> = ({ waitForUser = false }) => {
  const { currentUser, isAgency, isSaas } = useCurrentUser();
  const proId = currentUser?.id;
  const marketIds = currentUser?.professional?.markets?.map((m) => m.id) ?? [];
  const positionIds = currentUser?.professional?.positions?.map((p) => p.id) ?? [];

  const { isLoading, error, variant, shiftsCount, minPay, maxPay, availableJobsCount } =
    useSocialProofMarketActivity({
      marketIds,
      positionIds,
      currentUserId: proId ?? 0,
    });

  if (waitForUser && !currentUser) {
    return null;
  }

  if (isAgency || isSaas) {
    return null;
  }

  if (!proId || marketIds.length === 0 || positionIds.length === 0) {
    return null;
  }

  if (isLoading || error) {
    return null;
  }

  if (variant === undefined || shiftsCount === undefined || availableJobsCount === undefined) {
    return null;
  }

  let key: SocialProofVariant;

  if (shiftsCount === 0 || shiftsCount < 8) {
    key = 'low-volume';
  } else if (shiftsCount <= 60) {
    key = 'no-jobs';
  } else {
    key = 'high-volume';
  }

  const map: Record<SocialProofVariant, React.JSX.Element> = {
    'high-volume': (
      <HighVolume shiftsCount={shiftsCount} minPay={minPay ?? 0} maxPay={maxPay ?? 0} />
    ),
    'low-volume': <LowVolume />,
    'no-jobs': <NoJobs />,
  };

  return map[key];
};
